"use client";
import { IconButton } from "@ecco/ui/icon-button";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import type { LineItem } from "@graphql/generated/types";
import { Flex } from "@ecco/ui/flex";
import { useStore } from "@store/index";
import { useMemo, useRef } from "react";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import {
    createMyShoppingList,
    showErrorToaster,
    removeFromMyShoppingList,
    addToMyShoppingList,
} from "@lib/utils/shoppingList-utility/shoppingListUtility";
import {
    useAddProductToWishlist,
    useAddProductAndRemoveOldestFromWishlist,
    useRemoveProductFromWishlist,
} from "@lib/commerce/hooks/wishlistLineItemOperations";
import {
    useCreateMyWishlist,
    useGetMyActiveShoppingList,
} from "@lib/commerce/hooks/wishlistOperations";
import { useLock } from "@store/hooks";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useWishlistModalMicrocopies } from "@components/ui/modals/add-to-wishlist-modal/wishlistModalMicrocopy";
import { useAuth } from "@lib/auth/useAuth";
import { useLocale } from "next-intl";
import type { Product, ProductVariant } from "@lib/product/types";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

type WishlistVariant = "default" | "ghost";

export interface AddToWishlistProps {
    product: Product;
    size?: string;
    variant?: WishlistVariant;
}

const variantStyles = {
    default: {
        bg: "black",
        color: "white",
        variant: "solid" as const,
    },
    ghost: {
        bg: "transparent",
        color: "black",
        variant: "ghost" as const,
    },
};

export const AddToWishlist = ({
    product,
    size = "md",
    variant = "default",
}: AddToWishlistProps): JSX.Element => {
    const styles = variantStyles[variant];
    const { showModal } = useGlobalModalContext();
    const wishlist = useStore((state) => state.data.me.wishlistInfo.wishlist);
    const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
    const selectedVariant = activeProduct?.variant;
    const setWishlistIsExecuted = useStore((state) => state.setWishlistIsExecuted);
    const getWishlist = useStore((state) => state.getWishlist);
    const executeCreateMyWishlist = useCreateMyWishlist();
    const executeGetMyActiveWishlist = useGetMyActiveShoppingList();
    const executeAddProductToWishlist = useAddProductToWishlist();
    const executeAddProductAndRemoveOldestFromWishlist = useAddProductAndRemoveOldestFromWishlist();
    const executeRemoveProductFromWishlist = useRemoveProductFromWishlist();
    const { get: getMicrocopy } = useMicrocopy();
    const lock = useLock();
    const locale = useLocale();
    const { status } = useAuth();
    const errorToaster = useErrorToast();
    const wishlistButtonRef = useRef<HTMLButtonElement>(null);
    const wishlistModalMicrocopies = useWishlistModalMicrocopies();

    const handleAddToWishlist = lock(async (selectedVariant?: ProductVariant) => {
        setWishlistIsExecuted(false);

        await executeGetMyActiveWishlist();
        const currentWishlist = getWishlist();
        if (!currentWishlist) {
            await createMyShoppingList(locale, getMicrocopy, executeCreateMyWishlist, errorToaster);
        }

        try {
            await addToWishlist(product, selectedVariant);
        } catch {
            showErrorToaster(getMicrocopy, errorToaster);
        }
        setWishlistIsExecuted(true);
    });

    const addToWishlist = async (product: Product, selectedVariant?: ProductVariant) => {
        const wishlist = getWishlist();
        const foundItem = wishlist?.lineItems?.find((x) => x.productId === product.id);
        if (foundItem) {
            await removeFromMyShoppingList(
                locale,
                foundItem,
                wishlist,
                executeRemoveProductFromWishlist
            );
            void AnalyticsClient.removeCTProductFromWishlist(foundItem as unknown as LineItem);
        } else {
            await addToMyShoppingList(
                locale,
                wishlist,
                selectedVariant?.sku ?? product.sku,
                executeAddProductToWishlist,
                executeAddProductAndRemoveOldestFromWishlist
            );
            void AnalyticsClient.addCTProductToWishList(product);
        }
    };

    const foundItem = useMemo(() => {
        return wishlist?.lineItems?.find(
            (x) => x?.productId.toString() === product?.id?.toString()
        );
    }, [wishlist, product, selectedVariant]);
    return (
        <Flex mr={[0]}>
            <IconButton
                ref={wishlistButtonRef}
                disabled={status !== "authenticated"}
                size={size}
                aria-label="Add to wishlist"
                bg={styles.bg}
                color={styles.color}
                variant={styles.variant}
                data-testid="addToWishlist"
                icon={<ECCOIcon name={foundItem ? "wishlist-filled" : "wishlist"} />}
                data-dd-action-name={RUM_ACTION_NAME.ADD_TO_WISHLIST}
                onClick={() => {
                    showModal(ModalTypes.addToWishList, {
                        selectedProductSku: selectedVariant?.sku,
                        productKey: product.sku,
                        productId: product.id,
                        triggerRef: wishlistButtonRef,
                        microcopies: wishlistModalMicrocopies ?? undefined,
                    });
                    handleAddToWishlist(selectedVariant);
                }}
                cursor={"pointer"}
            />
        </Flex>
    );
};

export default AddToWishlist;
